/**
 * Query Evaluator for Patient Portal Content Targeting
 *
 * Supports:
 * - Basic comparisons: ==, !=
 * - Numeric comparisons: >, >=, <, <=
 * - Logical operators: &&, ||, !
 * - Parentheses for grouping
 * - Array operations: CONTAINS, CONTAINS_ANY, CONTAINS_ALL
 * - Array length: field.length
 * - Object array queries: ANY operator
 *
 * Examples:
 * - patient.state == IL
 * - patient.age > 45
 * - patient.pastGeneticTest.pathogenicMutations CONTAINS BRCA1
 * - patient.pastGeneticTest.pathogenicMutations CONTAINS_ANY [BRCA1, BRCA2]
 * - patient.cancers ANY (type == BREAST && tripleNegative == true)
 */

export type QueryContext = Record<string, unknown>;

type TokenType =
  | 'FIELD'
  | 'VALUE'
  | 'ARRAY_VALUE'
  | 'OPERATOR'
  | 'COMPARISON'
  | 'LOGICAL'
  | 'PAREN_OPEN'
  | 'PAREN_CLOSE'
  | 'NOT';

interface Token {
  type: TokenType;
  value: string;
}

interface ParseResult {
  value: boolean;
  nextIndex: number;
}

// Helper to get nested value from object using dot notation
export function getNestedValue(obj: Record<string, unknown>, path: string): unknown {
  // Handle .length accessor for arrays
  if (path.endsWith('.length')) {
    const basePath = path.slice(0, -7);
    const value = getNestedValue(obj, basePath);
    return Array.isArray(value) ? value.length : 0;
  }

  return path.split('.').reduce((current: unknown, key: string) => {
    if (current === null || current === undefined) return undefined;
    return (current as Record<string, unknown>)[key];
  }, obj);
}

// Parse a value string into the appropriate type
export function parseValue(value: string): unknown {
  if (value === 'true') return true;
  if (value === 'false') return false;
  if (value === 'null') return null;
  const num = parseFloat(value);
  if (!isNaN(num) && isFinite(num)) return num;
  return value;
}

// Parse array value string [val1, val2] into array
function parseArrayValue(arrayStr: string): unknown[] {
  return arrayStr.split(',').map((v) => parseValue(v.trim()));
}

// Tokenize query string
export function tokenizeQuery(query: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  const isWhitespace = (char: string) => /\s/.test(char);
  const isAlphanumeric = (char: string) => /[a-zA-Z0-9_.]/.test(char);

  while (i < query.length) {
    // Skip whitespace
    if (isWhitespace(query[i])) {
      i++;
      continue;
    }

    // Handle array literals [value1, value2]
    if (query[i] === '[') {
      let arrayContent = '';
      i++; // skip [
      while (i < query.length && query[i] !== ']') {
        arrayContent += query[i];
        i++;
      }
      i++; // skip ]
      tokens.push({ type: 'ARRAY_VALUE', value: arrayContent.trim() });
      continue;
    }

    // Handle parentheses
    if (query[i] === '(') {
      tokens.push({ type: 'PAREN_OPEN', value: '(' });
      i++;
      continue;
    }
    if (query[i] === ')') {
      tokens.push({ type: 'PAREN_CLOSE', value: ')' });
      i++;
      continue;
    }

    // Handle && operator
    if (query[i] === '&' && query[i + 1] === '&') {
      tokens.push({ type: 'LOGICAL', value: '&&' });
      i += 2;
      continue;
    }

    // Handle || operator
    if (query[i] === '|' && query[i + 1] === '|') {
      tokens.push({ type: 'LOGICAL', value: '||' });
      i += 2;
      continue;
    }

    // Handle ! (NOT) operator - must not be followed by =
    if (query[i] === '!' && query[i + 1] !== '=') {
      tokens.push({ type: 'NOT', value: '!' });
      i++;
      continue;
    }

    // Handle comparison operators
    if (query[i] === '=' && query[i + 1] === '=') {
      tokens.push({ type: 'COMPARISON', value: '==' });
      i += 2;
      continue;
    }
    if (query[i] === '!' && query[i + 1] === '=') {
      tokens.push({ type: 'COMPARISON', value: '!=' });
      i += 2;
      continue;
    }
    if (query[i] === '>' && query[i + 1] === '=') {
      tokens.push({ type: 'COMPARISON', value: '>=' });
      i += 2;
      continue;
    }
    if (query[i] === '<' && query[i + 1] === '=') {
      tokens.push({ type: 'COMPARISON', value: '<=' });
      i += 2;
      continue;
    }
    if (query[i] === '>') {
      tokens.push({ type: 'COMPARISON', value: '>' });
      i++;
      continue;
    }
    if (query[i] === '<') {
      tokens.push({ type: 'COMPARISON', value: '<' });
      i++;
      continue;
    }

    // Handle identifiers (field names, operators, values)
    if (isAlphanumeric(query[i])) {
      let identifier = '';
      while (i < query.length && isAlphanumeric(query[i])) {
        identifier += query[i];
        i++;
      }

      // Check if it's a special operator
      const upperIdentifier = identifier.toUpperCase();
      if (
        ['CONTAINS', 'CONTAINS_ANY', 'CONTAINS_ALL', 'ANY'].includes(
          upperIdentifier
        )
      ) {
        tokens.push({ type: 'OPERATOR', value: upperIdentifier });
      } else if (identifier.includes('.')) {
        // It's a field path
        tokens.push({ type: 'FIELD', value: identifier });
      } else {
        // Could be a field or a value - context will determine
        tokens.push({ type: 'VALUE', value: identifier });
      }
      continue;
    }

    // Skip unknown characters
    i++;
  }

  return tokens;
}

// Evaluate comparison
function evaluateComparison(
  leftValue: unknown,
  operator: string,
  rightValue: unknown
): boolean {
  switch (operator) {
    case '==':
      return (
        leftValue === rightValue ||
        String(leftValue) === String(rightValue)
      );
    case '!=':
      return (
        leftValue !== rightValue &&
        String(leftValue) !== String(rightValue)
      );
    case '>':
      return (
        typeof leftValue === 'number' &&
        typeof rightValue === 'number' &&
        leftValue > rightValue
      );
    case '>=':
      return (
        typeof leftValue === 'number' &&
        typeof rightValue === 'number' &&
        leftValue >= rightValue
      );
    case '<':
      return (
        typeof leftValue === 'number' &&
        typeof rightValue === 'number' &&
        leftValue < rightValue
      );
    case '<=':
      return (
        typeof leftValue === 'number' &&
        typeof rightValue === 'number' &&
        leftValue <= rightValue
      );
    default:
      return false;
  }
}

// Evaluate array operators
function evaluateArrayOperator(
  array: unknown,
  operator: string,
  values: unknown[]
): boolean {
  if (!Array.isArray(array)) return false;

  switch (operator) {
    case 'CONTAINS':
      return values.length === 1 && array.includes(values[0]);
    case 'CONTAINS_ANY':
      return values.some((v) => array.includes(v));
    case 'CONTAINS_ALL':
      return values.every((v) => array.includes(v));
    default:
      return false;
  }
}

// Parse OR expressions (lowest precedence)
function parseOr(
  tokens: Token[],
  index: number,
  context: QueryContext
): ParseResult {
  let result = parseAnd(tokens, index, context);

  while (
    result.nextIndex < tokens.length &&
    tokens[result.nextIndex]?.type === 'LOGICAL' &&
    tokens[result.nextIndex]?.value === '||'
  ) {
    const right = parseAnd(tokens, result.nextIndex + 1, context);
    result = {
      value: result.value || right.value,
      nextIndex: right.nextIndex,
    };
  }

  return result;
}

// Parse AND expressions (higher precedence than OR)
function parseAnd(
  tokens: Token[],
  index: number,
  context: QueryContext
): ParseResult {
  let result = parseNot(tokens, index, context);

  while (
    result.nextIndex < tokens.length &&
    tokens[result.nextIndex]?.type === 'LOGICAL' &&
    tokens[result.nextIndex]?.value === '&&'
  ) {
    const right = parseNot(tokens, result.nextIndex + 1, context);
    result = {
      value: result.value && right.value,
      nextIndex: right.nextIndex,
    };
  }

  return result;
}

// Parse NOT expressions (highest precedence)
function parseNot(
  tokens: Token[],
  index: number,
  context: QueryContext
): ParseResult {
  if (tokens[index]?.type === 'NOT') {
    const result = parseNot(tokens, index + 1, context);
    return { value: !result.value, nextIndex: result.nextIndex };
  }

  return parsePrimary(tokens, index, context);
}

// Parse primary expressions (conditions or parenthesized expressions)
function parsePrimary(
  tokens: Token[],
  index: number,
  context: QueryContext
): ParseResult {
  const token = tokens[index];

  if (!token) {
    return { value: false, nextIndex: index };
  }

  // Handle parenthesized expressions
  if (token.type === 'PAREN_OPEN') {
    const result = parseOr(tokens, index + 1, context);
    // Skip closing paren
    return { value: result.value, nextIndex: result.nextIndex + 1 };
  }

  // Handle field/value expressions
  if (token.type === 'FIELD' || token.type === 'VALUE') {
    return parseCondition(tokens, index, context);
  }

  return { value: false, nextIndex: index + 1 };
}

// Parse a condition (field comparison, array operation, or ANY)
function parseCondition(
  tokens: Token[],
  index: number,
  context: QueryContext
): ParseResult {
  const fieldToken = tokens[index];
  const fieldPath = fieldToken.value;

  // Check for next token
  const nextToken = tokens[index + 1];

  if (!nextToken) {
    // Just a field name - check for existence
    const value = getNestedValue(context, fieldPath);
    return {
      value: value !== undefined && value !== null,
      nextIndex: index + 1,
    };
  }

  // Handle comparison operators (==, !=, >, >=, <, <=)
  if (nextToken.type === 'COMPARISON') {
    const valueToken = tokens[index + 2];
    if (!valueToken) return { value: false, nextIndex: index + 2 };

    const leftValue = getNestedValue(context, fieldPath);
    const rightValue = parseValue(valueToken.value);

    return {
      value: evaluateComparison(leftValue, nextToken.value, rightValue),
      nextIndex: index + 3,
    };
  }

  // Handle array operators (CONTAINS, CONTAINS_ANY, CONTAINS_ALL)
  if (
    nextToken.type === 'OPERATOR' &&
    ['CONTAINS', 'CONTAINS_ANY', 'CONTAINS_ALL'].includes(nextToken.value)
  ) {
    const valueToken = tokens[index + 2];
    if (!valueToken) return { value: false, nextIndex: index + 2 };

    const array = getNestedValue(context, fieldPath);
    let values: unknown[];

    if (valueToken.type === 'ARRAY_VALUE') {
      values = parseArrayValue(valueToken.value);
    } else {
      values = [parseValue(valueToken.value)];
    }

    return {
      value: evaluateArrayOperator(array, nextToken.value, values),
      nextIndex: index + 3,
    };
  }

  // Handle ANY operator for object arrays
  // Syntax: field ANY (subquery)
  if (nextToken.type === 'OPERATOR' && nextToken.value === 'ANY') {
    const openParen = tokens[index + 2];
    if (openParen?.type !== 'PAREN_OPEN')
      return { value: false, nextIndex: index + 2 };

    const array = getNestedValue(context, fieldPath);
    if (!Array.isArray(array)) return { value: false, nextIndex: index + 3 };

    // Find the matching closing paren
    let parenDepth = 1;
    let closeParenIndex = index + 3;
    while (closeParenIndex < tokens.length && parenDepth > 0) {
      if (tokens[closeParenIndex].type === 'PAREN_OPEN') parenDepth++;
      if (tokens[closeParenIndex].type === 'PAREN_CLOSE') parenDepth--;
      closeParenIndex++;
    }

    // Extract sub-tokens for the inner query
    const subTokens = tokens.slice(index + 3, closeParenIndex - 1);

    // Evaluate the sub-query against each array element
    const result = array.some((item) => {
      // Each array item becomes the context root for the sub-query
      const subContext = item as QueryContext;
      const subResult = parseOr(subTokens, 0, subContext);
      return subResult.value;
    });

    return { value: result, nextIndex: closeParenIndex };
  }

  // Just a field - check existence
  const value = getNestedValue(context, fieldPath);
  return {
    value: value !== undefined && value !== null,
    nextIndex: index + 1,
  };
}

// Parse and evaluate expression
function parseExpression(tokens: Token[], context: QueryContext): ParseResult {
  return parseOr(tokens, 0, context);
}

/**
 * Evaluate a query string against a context object
 *
 * @param query - The query string to evaluate
 * @param context - The context object to evaluate against
 * @returns true if the query matches, false otherwise
 */
export function evaluateQuery(
  query: string,
  context: QueryContext
): boolean {
  if (!query || query.trim() === '') {
    return true; // Empty query means always show
  }

  try {
    const tokens = tokenizeQuery(query);

    if (tokens.length === 0) {
      return true;
    }

    const result = parseExpression(tokens, context);
    return result.value;
  } catch (e) {
    console.warn('Failed to evaluate query:', query, e);
    return false;
  }
}
